import {ThemeProvider as MuiThemeProvider} from '@mui/material/styles';
import type {TypographyStyle} from '@mui/material/styles';
import type {} from '@mui/lab/themeAugmentation';
import {createTheme, alpha} from '@mui/material';
import {LinkProps} from '@mui/material/Link';
import {LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterMoment} from '@mui/x-date-pickers/AdapterMoment';
import {useUserLanguage} from '@smartmixin/core';
// Leaf modules, not the barrel: see the note on the same import in tokens.ts.
import {
  colorRoles,
  type WeatherFamily,
} from '@smartmixin/core/src/theme/colorRoles';
import {mix} from '@smartmixin/core/src/utils/Colors';
import React, {PropsWithChildren} from 'react';
import {Link as RouterLink, LinkProps as RouterLinkProps} from 'react-router';
import {
  breakpoints,
  elevation,
  radius,
  semanticColors,
  typeScale,
  typography,
  navigation,
  secondaryFill,
  severityColors,
  severityMapColors,
  severityLaneColors,
} from './tokens';
import {useIsDarkTheme} from './useIsDarkTheme';

// Augment MUI theme to include custom colors
declare module '@mui/material/styles' {
  // (TypographyStyle allows nested media queries; React.CSSProperties does
  // not.)
  interface Palette {
    /**
     * The brand blue as an INK: links, text and outlined buttons, tab
     * indicator, switch-on, selected chip, radio. `primary.main` is the same
     * blue as a FILL and fails as text on light (2.36:1). `onInk` (also
     * `contrastText`) is the label on an ink fill.
     */
    ink: {main: string; onInk: string; contrastText: string};
    /**
     * The neutral button colour: `ui/Button` "secondary" (outlined, label in
     * text.primary). Its own key so MUI's `color="inherit"` keeps meaning
     * "inherit".
     */
    neutral: Palette['primary'];
    /** The one focus ring (2px, 2px offset). Same colour as `ink.main`. */
    focus: {ring: string};
    premium: {tint: string; onTint: string};
    /** Weather families: `tint` fills, `on` draws the line / glyph on it. */
    family: Record<WeatherFamily, {tint: string; on: string}>;
    dayCurve: {low: string; mid: string; high: string};
    /** Record marks on a chart (core `colorRoles.record`, = mobile maxValue/minValue). */
    record: {max: string; min: string};
    /**
     * `control` bounds anything operable (>= 3:1 on page and card);
     * `hairline` is decorative only and equals `divider`; `floating` is the
     * edge of a panel floating over a map (>= 3:1 on the basemap closest to
     * the theme's card: light on light, dark on dark).
     */
    border: {control: string; hairline: string; floating: string};
    chart: {grid: string};
    tooltip: {bg: string; fg: string};
    /** Dialog backdrop. */
    scrim: string;
    /**
     * Official weather warnings, one set per core `WarningTone`. See
     * `severityColors` in tokens.ts.
     */
    severity: (typeof severityColors)['light' | 'dark'];
    /** The warning map's strokes. See `severityMapColors` in tokens.ts. */
    severityMap: typeof severityMapColors;
    /** The warnings timeline. See `severityLaneColors` in tokens.ts. */
    severityLane: (typeof severityLaneColors)['light' | 'dark'];
  }
  interface PaletteOptions {
    ink?: Palette['ink'];
    neutral?: PaletteOptions['primary'];
    focus?: Palette['focus'];
    premium?: Palette['premium'];
    family?: Palette['family'];
    dayCurve?: Palette['dayCurve'];
    record?: Palette['record'];
    border?: Palette['border'];
    chart?: Palette['chart'];
    tooltip?: Palette['tooltip'];
    scrim?: Palette['scrim'];
    severity?: Palette['severity'];
    severityMap?: Palette['severityMap'];
    severityLane?: Palette['severityLane'];
  }

  // T2 type scale variants beyond MUI's own.
  interface TypographyVariants {
    data: TypographyStyle;
    title: TypographyStyle;
    kpi: TypographyStyle;
    hero: TypographyStyle;
    display: TypographyStyle;
    proBadge: TypographyStyle;
  }
  interface TypographyVariantsOptions {
    data?: TypographyStyle;
    title?: TypographyStyle;
    kpi?: TypographyStyle;
    hero?: TypographyStyle;
    display?: TypographyStyle;
    proBadge?: TypographyStyle;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    data: true;
    title: true;
    kpi: true;
    hero: true;
    display: true;
    proBadge: true;
  }
}

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & {href: RouterLinkProps['to']}
>((props, ref) => {
  const {href, ...other} = props;
  // Map href (Material UI) -> to (react-router)
  return <RouterLink ref={ref} to={href} {...other} />;
});

const rem = (px: number) => `${px / 16}rem`;
/**
 * Unitless line height from the spec's px pair (14/20 -> 1.4286). Unitless so
 * it scales with any local fontSize override instead of pinning a 20px line
 * that overlaps when a larger font wraps.
 */
const lh = ({fontSize, lineHeight}: {fontSize: number; lineHeight: number}) =>
  Number((lineHeight / fontSize).toFixed(4));
const TNUM = {fontVariantNumeric: 'tabular-nums'} as const;
/**
 * Roles whose elements carry tabindex=-1 while arrow keys move focus between
 * them (MUI MenuItem in the `menu` variant, the unselected Tabs, list
 * options). They still need the ring.
 */
const ROVING_ROLES = [
  'menuitem',
  'menuitemcheckbox',
  'menuitemradio',
  'option',
  'tab',
  'radio',
  'treeitem',
  'gridcell',
]
  .map((role) => `[role="${role}"]`)
  .join(', ');
const PHONE = '@media (max-width:599.95px)';

/**
 * The app's MUI theme, as a plain function of the one thing it depends on.
 *
 * Pulled out of the component's `useMemo` so it can be built without
 * rendering: `__tests__/a11y/contrast.test.ts` measures every role pair in
 * this palette against the WCAG AA floors, and it has to measure THIS theme
 * rather than a copy that drifts (#249). The memo below calls it with the same
 * single dependency, so the two cannot disagree.
 *
 * Every colour comes from a role: the shared ones from `colorRoles`
 * (@smartmixin/core), the webapp-only ones from `semanticColors` (tokens.ts).
 * The values and the component rules are the signed-off design-system spec
 * (`.scratch/webapp-design-system/target.html`, sections 1-3).
 *
 * `react-refresh/only-export-components` wants this in its own file, and it is
 * right in general. Not done here: the theme closes over `LinkBehavior`, so
 * splitting means either moving that component too or threading it in as an
 * argument. The cost is a dev-server nicety on a file that changes rarely.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const createAppTheme = (isDarkTheme: boolean) => {
  const mode = isDarkTheme ? 'dark' : 'light';
  const roles = colorRoles[mode];
  const web = semanticColors[mode];
  const ink = roles.ink.main;
  const control = web.line.control;
  const hairline = web.line.hairline;
  // Spec hover for a filled control: 88% fill, 12% its own label colour.
  const fillHover = mix(roles.brand.fill, roles.brand.onFill, 0.12).hex();
  const dangerHover = mix(roles.danger.main, roles.danger.onMain, 0.12).hex();
  const inkHover = mix(ink, roles.ink.onInk, 0.12).hex();
  const RING = {outline: `2px solid ${ink}`, outlineOffset: 2};

  const tint = (pair: {tint: string; onTint: string}) => ({
    backgroundColor: pair.tint,
    color: pair.onTint,
    '& .MuiAlert-icon': {color: pair.onTint},
  });

  return createTheme({
    breakpoints: {values: breakpoints},

    // Typography: T2. Inter variable (self-hosted), 14px body, 13px data, and
    // tabular figures on every number (set on <body> by CssBaseline below).
    // h2-h6 keep their previous sizes: the spec does not map MUI's heading
    // variants, and the screens using them move to `title` / `kpi` in their
    // own MRs.
    typography: {
      fontFamily: typography.fontFamily,
      // A 20px line and no tracking (MUI's defaults are 1.75 and 0.4px, which
      // made a "34px" button measure 36.5px).
      button: {
        textTransform: 'none',
        fontSize: rem(typeScale.body.fontSize),
        lineHeight: lh(typeScale.body),
        letterSpacing: 0,
        fontWeight: typography.weights.medium,
      },
      h1: {
        fontSize: rem(typeScale.h1.fontSize),
        lineHeight: lh(typeScale.h1),
        fontWeight: typeScale.h1.fontWeight,
        letterSpacing: `${typeScale.h1.letterSpacing}px`,
        [PHONE]: {fontSize: rem(typeScale.h1Phone.fontSize)},
      },
      h2: {
        fontWeight: typography.weights.bold,
        fontSize: typography.sizes['3xl'],
        lineHeight: typography.lineHeights.tight,
      },
      h3: {
        fontWeight: typography.weights.semibold,
        fontSize: typography.sizes['2xl'],
        lineHeight: typography.lineHeights.tight,
      },
      h4: {
        fontWeight: typography.weights.semibold,
        fontSize: typography.sizes.xl,
        lineHeight: typography.lineHeights.normal,
      },
      h5: {
        fontWeight: typography.weights.medium,
        fontSize: typography.sizes.lg,
        lineHeight: typography.lineHeights.normal,
      },
      h6: {
        fontWeight: typography.weights.medium,
        fontSize: typography.sizes.md,
        lineHeight: typography.lineHeights.normal,
      },
      body1: {
        fontSize: rem(typeScale.body.fontSize),
        lineHeight: lh(typeScale.body),
        fontWeight: typeScale.body.fontWeight,
      },
      body2: {
        fontSize: rem(typeScale.body.fontSize),
        lineHeight: lh(typeScale.body),
        fontWeight: typeScale.body.fontWeight,
      },
      caption: {
        fontSize: rem(typeScale.caption.fontSize),
        lineHeight: lh(typeScale.caption),
        fontWeight: typeScale.caption.fontWeight,
      },
      data: {
        fontSize: rem(typeScale.data.fontSize),
        lineHeight: lh(typeScale.data),
        fontWeight: typeScale.data.fontWeight,
        ...TNUM,
      },
      title: {
        fontSize: rem(typeScale.title.fontSize),
        lineHeight: lh(typeScale.title),
        fontWeight: typeScale.title.fontWeight,
      },
      kpi: {
        fontSize: rem(typeScale.kpi.fontSize),
        lineHeight: lh(typeScale.kpi),
        fontWeight: typeScale.kpi.fontWeight,
        ...TNUM,
      },
      hero: {
        fontSize: rem(typeScale.hero.fontSize),
        lineHeight: typeScale.hero.lineHeight,
        fontWeight: typeScale.hero.fontWeight,
        letterSpacing: `${typeScale.hero.letterSpacing}px`,
        ...TNUM,
      },
      display: {
        fontSize: rem(typeScale.display.fontSize),
        lineHeight: typeScale.display.lineHeight,
        fontWeight: typeScale.display.fontWeight,
        letterSpacing: `${typeScale.display.letterSpacing}px`,
        [PHONE]: {fontSize: rem(typeScale.displayPhone.fontSize)},
      },
      proBadge: {
        fontSize: rem(typeScale.proBadge.fontSize),
        lineHeight: typeScale.proBadge.lineHeight,
        fontWeight: typeScale.proBadge.fontWeight,
        letterSpacing: `${typeScale.proBadge.letterSpacing}px`,
      },
    },

    // Palette
    palette: {
      mode,
      // The brand blue as a FILL. Its label colour is a role, not MUI's
      // computed white (2.36:1 on light).
      primary: {main: roles.brand.fill, contrastText: roles.brand.onFill},
      secondary: {main: secondaryFill},
      error: {
        main: roles.danger.main,
        contrastText: roles.danger.onMain,
        light: roles.danger.tint,
        dark: roles.danger.onTint,
      },
      // `main` is the AA-passing ink tone of each status, so
      // `color="success.main"` text clears 4.5:1; light/dark are the banner
      // tint and the ink drawn on it.
      success: {
        main: roles.success.onTint,
        light: roles.success.tint,
        dark: roles.success.onTint,
      },
      info: {
        main: roles.info.onTint,
        light: roles.info.tint,
        dark: roles.info.onTint,
      },
      warning: {
        main: roles.warning.onTint,
        light: roles.warning.tint,
        dark: roles.warning.onTint,
      },
      ink: {main: ink, onInk: roles.ink.onInk, contrastText: roles.ink.onInk},
      neutral: {
        main: web.text.primary,
        light: web.text.primary,
        dark: web.text.primary,
        contrastText: web.surface.card,
      },
      focus: {ring: ink},
      premium: {...roles.premium},
      family: {
        temperature: {...roles.family.temperature},
        rain: {...roles.family.rain},
        wind: {...roles.family.wind},
        solar: {...roles.family.solar},
        overview: {...roles.family.overview},
        agro: {...roles.family.agro},
      },
      dayCurve: {...roles.dayCurve},
      record: {...roles.record},
      border: {control, hairline, floating: web.line.floating},
      chart: {...web.chart},
      tooltip: {...web.tooltip},
      scrim: web.scrim,
      severity: isDarkTheme ? severityColors.dark : severityColors.light,
      severityMap: severityMapColors,
      severityLane: isDarkTheme
        ? severityLaneColors.dark
        : severityLaneColors.light,
      background: {
        default: web.surface.page,
        paper: web.surface.card,
      },
      divider: hairline,
      text: {...web.text},
      action: {
        hover: web.action.hover,
        selected: web.action.selected,
        // Components that derive their own wash as alpha(primary, opacity)
        // land on the same 12% the `selected` role is.
        selectedOpacity: 0.12,
        disabled: web.text.disabled,
        disabledBackground: web.action.disabledBackground,
      },
    },

    // Shape: radius.md (cards). Unchanged from before, deliberately: MUI
    // multiplies `sx={{borderRadius: n}}` by this, so moving it would re-round
    // every screen. Buttons and inputs take radius.sm explicitly below.
    shape: {
      borderRadius: radius.md,
    },

    // Component overrides
    components: {
      // One focus ring for the whole app: 2px ink, 2px offset. The offset is
      // not optional — on dark the ring IS the brand blue, 1.00:1 against a
      // filled button, so without the gap it would be invisible there.
      //
      // `html :focus-visible:not([tabindex="-1"])` (0,2,1) outranks the
      // `outline: 0` MUI puts on ButtonBase and InputBase with a single class
      // (0,1,0). Elements with tabindex="-1" are excluded -- they are
      // programmatic focus targets (dialog containers, the help-image
      // lightbox), never tabbed to, and a ring around a whole modal would be
      // noise -- EXCEPT roving-tabindex items, which arrow keys move between. Outlined inputs show focus
      // as an ink border instead (MuiOutlinedInput below), so the inner
      // <input> does not draw a second indicator inside it.
      MuiCssBaseline: {
        styleOverrides: {
          body: TNUM,
          // Three rules rather than one selector list: a browser that cannot
          // parse one selector in a list drops the WHOLE rule, and the
          // general ring must never be collateral.
          'html :focus-visible:not([tabindex="-1"])': RING,
          [`html [tabindex="-1"]:is(${ROVING_ROLES}):focus-visible`]: RING,
          // Checkbox / radio / switch: focus lands on an opacity-0 <input>,
          // so the ring goes on the visible root MUI marks focus-visible.
          [[
            'html .MuiCheckbox-root.Mui-focusVisible',
            'html .MuiRadio-root.Mui-focusVisible',
            'html .MuiSwitch-switchBase.Mui-focusVisible',
          ].join(', ')]: RING,
          'html .MuiOutlinedInput-input:focus-visible': {
            outline: 'none',
          },
          // Tabs sit in an overflow:hidden strip exactly their height, which
          // would clip an outside ring; tabs have no fill, so inset it.
          'html .MuiTab-root:focus-visible': {
            outlineOffset: -2,
          },
          // maplibre's credit line is white at 50% over the map, so basemap
          // labels show through it. An opaque card strip in text.secondary
          // (5.94:1 light, 6.89:1 dark), links underlined rather than told
          // apart by nothing. (0,2,1)/(0,2,2) outrank maplibre-gl.css's
          // (0,2,0) background and (0,1,1) link rules whatever the load
          // order. The compact form (an "i" toggle) is left to maplibre.
          'html .maplibregl-ctrl-attrib:not(.maplibregl-compact)': {
            backgroundColor: web.surface.card,
            color: web.text.secondary,
            '& a': {color: 'inherit', textDecoration: 'underline'},
          },
        },
      },

      // Link behavior for router; links are ink, never the brand fill.
      MuiLink: {
        defaultProps: {
          component: LinkBehavior,
        } as LinkProps,
        styleOverrides: {
          root: {
            variants: [
              {
                props: {color: 'primary'},
                style: {color: ink, textDecorationColor: alpha(ink, 0.4)},
              },
            ],
          },
        },
      },
      MuiButtonBase: {
        defaultProps: {
          LinkComponent: LinkBehavior,
        },
      },

      // Card: resting = hairline, no shadow. The hover edge (line.control)
      // belongs to cards that are controls only: ui/Surface adds it with
      // `onClick`, so a static card does not look clickable.
      MuiCard: {
        defaultProps: {
          variant: 'outlined',
        },
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            boxShadow: 'none',
            borderColor: hairline,
            transition: 'border-color 200ms',
          },
        },
      },

      // Paper styling.
      // Intentionally do NOT default Paper to `outlined`: that gives every
      // Paper-based surface (AppBar, the permanent Drawers, menus, dialogs,
      // popovers) a 1px border and a rounded corner, rounding full-bleed
      // surfaces that must stay square. Paper keeps MUI's default `elevation`
      // variant (matches prod); the `outlined` overrides below still apply to
      // any Paper explicitly set to that variant. Cards have their own
      // `outlined` default (MuiCard) and are unaffected.
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          outlined: {
            borderRadius: radius.md,
            borderColor: hairline,
          },
        },
      },

      // Buttons: contained = brand fill + its label; text and outlined = ink,
      // outlined bounded by line.control; danger = danger.main / onMain.
      MuiButton: {
        defaultProps: {
          disableElevation: true,
          disableFocusRipple: true,
        },
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            minHeight: 34,
            padding: '6px 12px',
            fontWeight: typography.weights.medium,
            variants: [
              {
                props: {size: 'small'},
                style: {minHeight: 28, padding: '4px 10px'},
              },
              {
                props: {size: 'large'},
                style: {minHeight: 40, padding: '8px 16px'},
              },
              // Outlined: 1px less padding for the 1px border, so an outlined
              // button is exactly as tall and wide as a contained one.
              {props: {variant: 'outlined'}, style: {padding: '5px 11px'}},
              {
                props: {variant: 'outlined', size: 'small'},
                style: {padding: '3px 9px'},
              },
              {
                props: {variant: 'outlined', size: 'large'},
                style: {padding: '7px 15px'},
              },
              {
                props: {variant: 'contained', color: 'primary'},
                style: {
                  backgroundColor: roles.brand.fill,
                  color: roles.brand.onFill,
                  '@media (hover: hover)': {
                    '&:hover': {backgroundColor: fillHover},
                  },
                },
              },
              {
                props: {variant: 'contained', color: 'error'},
                style: {
                  backgroundColor: roles.danger.main,
                  color: roles.danger.onMain,
                  '@media (hover: hover)': {
                    '&:hover': {backgroundColor: dangerHover},
                  },
                },
              },
              {
                props: {variant: 'text', color: 'primary'},
                style: {
                  color: ink,
                  '&:hover': {backgroundColor: web.action.hover},
                },
              },
              {
                props: {variant: 'outlined'},
                style: {borderColor: control},
              },
              {
                props: {variant: 'outlined', color: 'primary'},
                style: {
                  color: ink,
                  borderColor: control,
                  '&:hover': {
                    borderColor: control,
                    backgroundColor: web.action.hover,
                  },
                },
              },
              // ui/Button "secondary": a neutral outline, label in text.primary.
              {
                props: {variant: 'outlined', color: 'neutral'},
                style: {
                  color: web.text.primary,
                  borderColor: control,
                  '&:hover': {
                    borderColor: control,
                    backgroundColor: web.action.hover,
                  },
                },
              },
              {
                props: {variant: 'outlined', color: 'error'},
                style: {borderColor: roles.danger.main},
              },
            ],
          },
        },
      },

      // TextField styling
      MuiTextField: {
        defaultProps: {
          variant: 'outlined',
          size: 'small',
        },
      },
      // Inputs: boundary line.control (>= 3:1 on page and card); focused
      // border ink, 2px; error border danger even while focused.
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: control,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: ink,
              borderWidth: 2,
            },
            '&.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderColor: roles.danger.main,
            },
          },
        },
      },

      // Selection controls: on = ink.
      MuiCheckbox: {
        defaultProps: {disableFocusRipple: true},
        styleOverrides: {
          root: {
            variants: [
              {
                props: {color: 'primary'},
                style: {'&.Mui-checked': {color: ink}},
              },
            ],
          },
        },
      },
      MuiRadio: {
        defaultProps: {disableFocusRipple: true},
        styleOverrides: {
          root: {
            variants: [
              {
                props: {color: 'primary'},
                style: {'&.Mui-checked': {color: ink}},
              },
            ],
          },
        },
      },
      MuiSwitch: {
        defaultProps: {disableFocusRipple: true},
        styleOverrides: {
          switchBase: {
            variants: [
              {
                props: {color: 'primary'},
                style: {
                  '&.Mui-checked': {color: ink},
                  '&.Mui-checked + .MuiSwitch-track': {backgroundColor: ink},
                },
              },
            ],
          },
        },
      },
      // Sliders (map playback, builder opacity, the moon's sky dome): the
      // keyboard focus is the app's ring on the thumb. MUI's default is a
      // 14% halo of the slider's own colour, barely visible on the card; the
      // focused element is the visually hidden <input>, so the global ring
      // rule cannot show either.
      MuiSlider: {
        styleOverrides: {
          thumb: {
            '&.Mui-focusVisible': {
              boxShadow: 'none',
              outlineStyle: 'solid',
              outlineWidth: 2,
              outlineColor: ink,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiToggleButton: {
        defaultProps: {disableFocusRipple: true},
      },

      // Chips: selected (primary) = ink / onInk.
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: radius.xs,
            fontWeight: typography.weights.medium,
            variants: [
              {
                props: {variant: 'filled', color: 'primary'},
                style: {
                  backgroundColor: ink,
                  color: roles.ink.onInk,
                  // MUI's own clickable hover/focus wash is primary.dark (the
                  // brand fill darkened), which the onInk label fails on.
                  '&.MuiChip-clickable:hover, &.Mui-focusVisible': {
                    backgroundColor: inkHover,
                  },
                },
              },
              {
                props: {variant: 'outlined', color: 'primary'},
                style: {color: ink, borderColor: ink},
              },
            ],
          },
        },
      },

      // Skeleton blocks are chart.grid; a 1.2s pulse, still under
      // prefers-reduced-motion. MUI's own reduced-motion styles only apply
      // when `theme.motion.reducedMotion` is 'system' or 'always'
      // (styles/reducedMotion.js); its default is 'never', so the media block
      // here is what stops it.
      MuiSkeleton: {
        defaultProps: {
          animation: 'pulse',
        },
        styleOverrides: {
          root: {
            borderRadius: radius.xs,
            backgroundColor: web.chart.grid,
            '&.MuiSkeleton-pulse': {animationDuration: '1.2s'},
            '@media (prefers-reduced-motion: reduce)': {
              '&.MuiSkeleton-pulse, &.MuiSkeleton-wave::after': {
                animation: 'none',
              },
            },
          },
        },
      },

      // Tabs: 2px ink indicator; selected label text.primary, rest secondary.
      MuiTab: {
        defaultProps: {disableFocusRipple: true},
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: typography.weights.medium,
            minHeight: 36,
            padding: '6px 10px',
          },
          textColorPrimary: {
            color: web.text.secondary,
            '&.Mui-selected': {color: web.text.primary},
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: {minHeight: 36},
          indicator: {
            height: 2,
            backgroundColor: ink,
          },
        },
      },

      // List item styling
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: radius.md,
            margin: '2px 8px',
            padding: '8px 12px',
            '&.Mui-selected': {
              backgroundColor: web.action.selected,
              '&:hover': {
                backgroundColor: alpha(roles.brand.fill, 0.18),
              },
            },
          },
        },
      },

      // Drawer styling
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${hairline}`,
          },
        },
      },

      // AppBar styling
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundColor: web.surface.card,
            borderBottom: `1px solid ${hairline}`,
          },
        },
      },

      // Tooltip: inverse.
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: web.tooltip.bg,
            color: web.tooltip.fg,
            borderRadius: radius.xs,
            fontSize: rem(typeScale.caption.fontSize),
            fontWeight: typography.weights.medium,
          },
          arrow: {
            color: web.tooltip.bg,
          },
        },
      },

      // Dialog: modal elevation over the scrim.
      MuiDialog: {
        styleOverrides: {
          root: {
            '& .MuiDialog-backdrop:not(.MuiBackdrop-invisible)': {
              backgroundColor: web.scrim,
            },
          },
          paper: {
            borderRadius: radius.lg,
            boxShadow: elevation.modal,
          },
        },
      },
      // Dialog parts: padding 20, title 17/600, actions right with gap 8.
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            padding: '20px 20px 8px',
            fontSize: rem(17),
            lineHeight: 1.4,
            fontWeight: typography.weights.semibold,
            color: web.text.primary,
          },
        },
      },
      // Theme overrides land AFTER MUI's own variants, so the `dividers`
      // padding has to be restated here or it is lost.
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '4px 20px 8px',
            variants: [
              {props: {dividers: true}, style: {padding: '16px 20px'}},
            ],
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '12px 20px 20px',
            gap: 8,
            '& > :not(style) ~ :not(style)': {marginLeft: 0},
          },
        },
      },

      // Alert: the status tint with its own ink (banner).
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            // 10/12 visually: MUI pads the message 8px vertically itself.
            padding: '2px 12px',
            variants: [
              {
                props: {variant: 'standard', severity: 'success'},
                style: tint(roles.success),
              },
              {
                props: {variant: 'standard', severity: 'info'},
                style: tint(roles.info),
              },
              {
                props: {variant: 'standard', severity: 'warning'},
                style: tint(roles.warning),
              },
              {
                props: {variant: 'standard', severity: 'error'},
                style: tint(roles.danger),
              },
            ],
          },
        },
      },

      // Toolbar
      MuiToolbar: {
        styleOverrides: {
          root: {
            minHeight: navigation.appBarHeight,
            '@media (max-width: 600px)': {
              minHeight: navigation.appBarHeightMobile,
            },
          },
        },
      },

      // IconButton: icons in text.secondary; the ring is the focus indicator.
      MuiIconButton: {
        defaultProps: {disableFocusRipple: true},
        styleOverrides: {
          root: {
            borderRadius: radius.sm,
            variants: [
              {props: {color: 'default'}, style: {color: web.text.secondary}},
            ],
          },
        },
      },

      // BottomNavigation
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: navigation.bottomNavHeight,
            borderTop: `1px solid ${hairline}`,
          },
        },
      },
      MuiBottomNavigationAction: {
        styleOverrides: {
          root: {
            minWidth: 64,
            padding: '6px 12px',
            // The selected label is text: ink, not the brand fill (2.36:1).
            '&.Mui-selected': {
              color: ink,
            },
          },
          label: {
            fontSize: typography.sizes.xs,
            '&.Mui-selected': {
              fontSize: typography.sizes.xs,
            },
          },
        },
      },
    },
  });
};

const ThemeProvider = ({children}: PropsWithChildren) => {
  const isDarkTheme = useIsDarkTheme();
  const language = useUserLanguage();

  const theme = React.useMemo(() => createAppTheme(isDarkTheme), [isDarkTheme]);

  return (
    <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={language}>
      <MuiThemeProvider theme={theme}>{children}</MuiThemeProvider>
    </LocalizationProvider>
  );
};

export default ThemeProvider;
